/* eslint-disable sonarjs/no-duplicate-string */
import * as Yup from 'yup';
import { TranslationProps } from 'ff-shared-components-core';
import { SubscriptionTypes, PermanentCertificateTypes } from '@store';
import * as Helpers from '../helpers';

export const entityDataSchema = (
	props: TranslationProps
): Yup.ObjectSchema<Yup.Shape<
	object,
	{
		nipc: string;
		entityName: string;
		address: string;
		postalCodePrefix: string;
		postalCodeSuffix: string;
		locality: string;
		preferredContactName: string;
		preferredContactEmail: string;
		preferredContactPhonePrefix: string;
		preferredContactPhoneNumber: string;
		subscription: SubscriptionTypes;
		typePermanentCertificate: PermanentCertificateTypes;
		permanentCertificateFile: object;
		permanentCertificateCode: string;
	}
>> => {
	const { t } = props;
	const minLength: string = t('errors.form.min-length');
	const maxLength: string = t('errors.form.max-length');
	const required: string = t('errors.form.required');
	const notValid: string = t('errors.form.not-valid');

	return Yup.object().shape({
		nipc: Yup.string()
			.matches(/\d{9}/, t('errors.form.must-contain-9digits'))
			.required(required),
		entityName: Yup.string()
			.min(2, minLength)
			.max(50, maxLength)
			.required(required),
		address: Yup.string()
			.min(2, minLength)
			.max(50, maxLength)
			.required(required),
		postalCodePrefix: Yup.string()
			.matches(/\d{4}/, notValid)
			.required(t('errors.form.required')),
		postalCodeSuffix: Yup.string()
			.matches(/\d{3}/, notValid)
			.max(3, t('Tamanho máximo de 3 caracteres'))
			.required(required),
		locality: Yup.string()
			.min(2, minLength)
			.max(30, maxLength)
			.required(required),
		preferredContactName: Yup.string()
			.min(2, minLength)
			.max(50, maxLength)
			.required(required),
		preferredContactEmail: Yup.string()
			.email(notValid)
			.required(required),
		preferredContactPhonePrefix: Yup.string()
			.required(required)
			.matches(/^[+][0-9]{2,3}$/, notValid)
			.max(4, t('errors.form.max-length-4c')),
		preferredContactPhoneNumber: Yup.string()
			.matches(/\d{9}/, t('errors.form.must-contain-9digits'))
			.required(required),
		subscription: Yup.mixed().required(required),
		typePermanentCertificate: Yup.mixed().when('nipc', (nipc: string) => {
			const isPrivateNipc = Helpers.isPrivateNipc(nipc);
			return isPrivateNipc ? Yup.mixed() : Yup.mixed().required(required);
		}),
		permanentCertificateFile: Yup.object()
			.test('valid', 'shouldBeShown', function(v) {
				const nipcRef = Yup.ref('nipc');
				const typePermanentCertificateRef = Yup.ref('typePermanentCertificate');
				const nipcValue = this.resolve(nipcRef);
				const typePermanentCertificateValue = this.resolve(typePermanentCertificateRef);

				const isPrivateNipc = Helpers.isPrivateNipc(nipcValue);
				const isPublicFile = typePermanentCertificateValue === PermanentCertificateTypes.file;
				const isValidValue = v && v.name && v.name.toString().trim().length > 0;

				return (
					(isPrivateNipc && isValidValue) ||
					(!isPrivateNipc && ((isPublicFile && isValidValue) || !isPublicFile))
				);
			})
			.required(required),
		permanentCertificateCode: Yup.string().when('typePermanentCertificate', {
			is: PermanentCertificateTypes.code, // 'code',
			then: Yup.string()
				.matches(/[\d/-]{12}|[\d/-]{14}/, t('errors.form.must-contain-14chars'))
				.min(12, t('errors.form.must-contain-14chars'))
				.max(14, t('errors.form.must-contain-14chars'))
				.required(required),
			otherwise: Yup.string(),
		}),
	});
};
